import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { XCircle, RefreshCw, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import { getBookingDetails } from "../api/services/guestService";
import { getKeywordBasedSearch } from "../api/services/hostService";

const PaymentFailure = () => {
  const { booking_id } = useParams<{ booking_id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<any>(null);
  const [property, setProperty] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [errorReason, setErrorReason] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const b = await getBookingDetails(booking_id!);
        setBooking(b);
        if (b?.property_id) {
          const p = await getKeywordBasedSearch({
            property_id: b.property_id,
            keyword: "",
            checkin: b.check_in,
            checkout: b.check_out,
            accommodation_type: "",
          });
          setProperty(Array.isArray(p) ? p[0] : p);
        }
      } catch {
        // non-fatal - show what we can
      } finally {
        setLoading(false);
      }
    };
    if (booking_id) load();
  }, [booking_id]);

  useEffect(() => {
    const reason = localStorage.getItem("payment_error_reason");
    if (reason) {
      setErrorReason(reason);
      localStorage.removeItem("payment_error_reason");
    }
  }, []);

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-";

  const amount = booking?.price_with_gst
    ? Number(booking.price_with_gst).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "-";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full"
        >
          {/* Failure Card */}
          <div className="bg-card rounded-3xl shadow-2xl overflow-hidden border border-border">
            {/* Failure Header */}
            <div className="bg-gradient-to-br from-red-500 to-rose-600 p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
              >
                <XCircle className="w-12 h-12 text-red-500" />
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl md:text-3xl font-bold text-white mb-2"
              >
                Payment Failed
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-white/90"
              >
                There was an issue processing your payment.
              </motion.p>
            </div>

            {/* Booking Summary */}
            <div className="p-6 md:p-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-4"
              >
                {/* Help Text */}
                <p className="text-sm text-muted-foreground text-center">
                  Please{" "}
                  <span className="text-primary font-medium">try again</span> or{" "}
                  <Link to="/contact" className="text-primary hover:underline font-medium">
                    contact support
                  </Link>{" "}
                  if the problem persists.
                </p>

                {errorReason && (
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-xs text-red-700 dark:text-red-400 font-mono break-all">
                      Error: {errorReason}
                    </p>
                  </div>
                )}

                <h2 className="text-lg font-semibold text-foreground pt-2">
                  Booking Summary
                </h2>

                {loading ? (
                  <div className="py-6 flex justify-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Booking ID */}
                    <div className="py-3 border-b border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Booking ID</p>
                      <p className="text-foreground font-medium">{booking_id}</p>
                    </div>

                    {/* Property */}
                    <div className="py-3 border-b border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Property</p>
                      <p className="text-foreground font-medium">{property?.name ?? "-"}</p>
                    </div>

                    {/* Dates Row */}
                    <div className="grid grid-cols-2 gap-4 py-3 border-b border-border">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Check-in</p>
                        <p className="text-foreground font-medium">{formatDate(booking?.check_in)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Check-out</p>
                        <p className="text-foreground font-medium">{formatDate(booking?.check_out)}</p>
                      </div>
                    </div>

                    {/* Amount Attempted */}
                    <div className="py-3 flex justify-between items-center">
                      <span className="font-semibold text-foreground">Amount Attempted</span>
                      <span className="font-semibold text-red-600 dark:text-red-500 text-lg">₹{amount}</span>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full gap-2">
                      <Home className="w-4 h-4" />
                      Go Home
                    </Button>
                  </Link>
                  <Button
                    className="flex-1 gap-2 bg-primary hover:bg-primary/90"
                    onClick={() => navigate(-1)}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Help Text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center text-muted-foreground text-sm mt-6"
          >
            Still facing issues?{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact Support
            </Link>
          </motion.p>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentFailure;
